'use client';

import { useContext, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSWR from 'swr';
import { useSWRConfig } from 'swr';
import axiosInstance from '@/lib/axios';
import { revalidateAfterCommunityBusinessListingChange } from '@/lib/revalidate-cache';
import { toast } from 'sonner';
import { CommunityContext } from '@/providers/community-context';
import { useUser } from '@/hooks/use-user';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, Loader2, Link2, Plus, Unlink, RefreshCw, Link as LinkIcon } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import axios from 'axios';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import type { Business, Community, CommunityBusinessLink } from '@/types';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

function getApiErrorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const d = e.response?.data as unknown;
    if (d && typeof d === 'object' && d !== null && 'error' in d) {
      const msg = (d as { error: unknown }).error;
      if (msg != null && String(msg).trim() !== '') return String(msg);
    }
    if (typeof d === 'string' && d.trim() !== '') return d;
  }
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

type BusinessLinksResponse = {
  links: CommunityBusinessLink[];
  limits?: {
    maxPerMember: number | null;
    minPerMember: number | null;
    activeApprovedOrPending: number;
  };
};

const statusLabelDefault: Record<string, string> = {
  PENDING: 'Pending approval',
  APPROVED: 'Listed',
  REJECTED: 'Rejected — you can request again',
};

const statusLabelCompact: Record<string, string> = {
  PENDING: 'Waiting',
  APPROVED: 'Listed',
  REJECTED: 'Rejected',
};

export type CommunityMyBusinessLinksProps = {
  /** When set (e.g. compliance modal at app root), works without CommunityLayout / CommunityContext. */
  community?: Community;
  /** Override card title (default: Directory listings). */
  title?: string;
  /** Override card description. */
  description?: string;
  /** Compact copy/layout for the compliance modal. Business page keeps default. */
  presentation?: 'default' | 'compact';
};

/** Curated directory listings UI. Used in `BusinessView` (via context) and `BusinessLinkCompliancePrompt` (with `community` prop). */
export function CommunityMyBusinessLinks({
  community: communityProp,
  title,
  description,
  presentation = 'default',
}: CommunityMyBusinessLinksProps) {
  const isCompact = presentation === 'compact';
  const resolvedTitle = title ?? (isCompact ? 'Your businesses' : 'Directory listings');

  const ctx = useContext(CommunityContext);
  const community = communityProp ?? ctx?.community;

  const { user } = useUser();
  const { mutate: globalMutate } = useSWRConfig();
  const [addingId, setAddingId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const businessLinksSwrKey =
    community?.controlledBusinessListing && community.id
      ? `/api/communities/${community.id}/business-links`
      : null;

  const { data, error, isLoading, mutate } = useSWR<BusinessLinksResponse>(
    businessLinksSwrKey,
    fetcher
  );

  const links = useMemo(() => data?.links ?? [], [data?.links]);

  const linksByBusinessId = useMemo(() => {
    const m = new Map<string, CommunityBusinessLink>();
    links.forEach((l) => m.set(l.businessId, l));
    return m;
  }, [links]);

  const unlinkedBusinesses = useMemo(() => {
    const myBusinesses = (user?.businesses ?? []) as Business[];
    return myBusinesses.filter((b) => !linksByBusinessId.has(b.id));
  }, [user?.businesses, linksByBusinessId]);

  const handleAdd = async (businessId: string) => {
    const cid = community?.id;
    if (!cid || !community) return;
    setAddingId(businessId);
    try {
      await axiosInstance.post(`/api/communities/${cid}/business-links`, {
        businessIds: [businessId],
      });
      toast.success(
        community.businessLinkApprovalRequired
          ? 'Listing request sent for admin approval'
          : 'Business added to directory'
      );
      await Promise.all([
        mutate(),
        revalidateAfterCommunityBusinessListingChange(globalMutate, cid),
      ]);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not add listing'));
    } finally {
      setAddingId(null);
    }
  };

  const handleRemove = async (businessId: string) => {
    const cid = community?.id;
    if (!cid) return;
    setRemovingId(businessId);
    try {
      await axiosInstance.delete(`/api/communities/${cid}/business-links`, {
        data: { businessIds: [businessId] },
      });
      toast.success('Listing removed');
      await Promise.all([
        mutate(),
        revalidateAfterCommunityBusinessListingChange(globalMutate, cid),
      ]);
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Could not remove listing'));
    } finally {
      setRemovingId(null);
    }
  };

  if (!community) {
    return null;
  }

  if (!community.controlledBusinessListing) {
    return null;
  }

  if (isLoading) {
    if (isCompact) {
      return (
        <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading…
        </div>
      );
    }
    return (
      <Card className="border-dashed">
        <CardContent className="flex items-center gap-2 py-8 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading your directory listings…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    if (isCompact) {
      return (
        <p className="py-6 text-center text-sm text-destructive">
          Could not load. Try again later.
        </p>
      );
    }
    return (
      <Card className="border-destructive/40">
        <CardContent className="py-6 text-sm text-destructive">
          Could not load directory listings. Try again later.
        </CardContent>
      </Card>
    );
  }

  /** Prefer server limits (matches POST enforcement); context can be stale or missing fields. */
  const maxPerMemberRaw =
    data?.limits?.maxPerMember ??
    (community.businessLinksMaxPerMember != null ? community.businessLinksMaxPerMember : null);
  const maxN =
    maxPerMemberRaw != null && String(maxPerMemberRaw).trim() !== ''
      ? Number(maxPerMemberRaw)
      : null;
  const activeFromApi = data?.limits?.activeApprovedOrPending;
  const activeCount =
    typeof activeFromApi === 'number' && Number.isFinite(activeFromApi)
      ? activeFromApi
      : links.filter((l) => l.status === 'APPROVED' || l.status === 'PENDING').length;
  const atCap = maxN != null && Number.isFinite(maxN) && activeCount >= maxN;
  const createBusinessHref = `/${community.id}/create/business`;
  const statusLabel = isCompact ? statusLabelCompact : statusLabelDefault;
  const hasNoBusinesses = links.length === 0 && unlinkedBusinesses.length === 0;
  const resolvedDescription =
    description ??
    (isCompact
      ? `Click connect to connect your business to ${community.name}.`
      : 'Choose which of your businesses appear in the public directory.');

  if (isCompact && hasNoBusinesses) {
    return (
      <div className="flex flex-col items-center text-center gap-5 px-2 py-2">
        <Link
          to={createBusinessHref}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 transition-colors hover:bg-primary/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Create business"
        >
          <Building2 className="h-7 w-7 text-primary" />
        </Link>
        <div className="space-y-1.5 max-w-sm">
          <p className="text-base font-semibold text-foreground">No businesses yet</p>
          <p className="text-sm text-muted-foreground leading-relaxed">
            Create a business profile to list it in this community.
          </p>
        </div>
        <Button asChild size="lg" className="w-full max-w-sm gap-2">
          <Link to={createBusinessHref}>
            <Plus className="h-4 w-4" />
            Create business
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <Card className="overflow-hidden border-primary/15 bg-gradient-to-br from-card to-primary/5">
      <CardHeader className={isCompact ? 'space-y-2 p-4 pb-0' : 'pb-2'}>
        <div className="flex items-center gap-2">
          <Link2 className="h-5 w-5 text-primary" />
          <CardTitle className="text-lg">{resolvedTitle}</CardTitle>
        </div>
        <CardDescription>
          {resolvedDescription}
          {!isCompact && maxN != null && Number.isFinite(maxN) && (
            <span className="block text-xs mt-1 text-muted-foreground">
              Listings used: {Math.min(activeCount, maxN)} / {maxN} (approved or pending)
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className={isCompact ? 'space-y-4 p-4' : 'space-y-4'}>
        {hasNoBusinesses ? (
          <p className="text-sm text-muted-foreground">
            Create a business profile, then list it in this community directory.
          </p>
        ) : null}

        {links.length > 0 && (
          <ul className="space-y-2">
            {links.map((link) => {
              const isRejected = link.status === 'REJECTED';
              return (
                <li
                  key={link.id}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-background/80 px-3 py-2"
                >
                  <div className="min-w-0">
                    <p className="font-medium truncate">{link.businessName}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <Badge
                        variant={
                          link.status === 'APPROVED'
                            ? 'default'
                            : isRejected
                              ? 'destructive'
                              : 'secondary'
                        }
                      >
                        {statusLabel[link.status] ?? link.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {isRejected && (
                      <Button
                        type="button"
                        variant="default"
                        size="sm"
                        className="gap-1"
                        disabled={atCap || addingId === link.businessId}
                        onClick={() => handleAdd(link.businessId)}
                      >
                        {addingId === link.businessId ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <RefreshCw className="h-4 w-4" />
                        )}
                        {isCompact ? 'Try again' : 'Request again'}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      disabled={removingId === link.businessId}
                      onClick={() => handleRemove(link.businessId)}
                    >
                      {removingId === link.businessId ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Unlink className="h-4 w-4" />
                      )}
                      Remove
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {unlinkedBusinesses.length > 0 && (
          <div className={isCompact ? undefined : 'space-y-2'}>
            {!isCompact ? <p className="text-sm font-medium">List an existing business</p> : null}
            {isCompact ? (
              <ul className="divide-y divide-foreground/25 border-y border-foreground/25">
                {unlinkedBusinesses.map((business) => {
                  const categoryLabel = formatBusinessCategoryLabel(
                    business.category ?? business.businessCategory,
                    business.subCategory,
                    business.selfDefinedCategory
                  );
                  return (
                    <li key={business.id} className="flex items-center gap-3 py-4">
                      <Avatar className="h-10 w-10 shrink-0 rounded-lg">
                        <AvatarImage src={business.logo || undefined} alt={business.name} />
                        <AvatarFallback className="rounded-lg text-sm font-semibold">
                          {business.name?.[0]?.toUpperCase() || 'B'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium truncate text-foreground">{business.name}</p>
                        {categoryLabel ? (
                          <p className="text-sm text-muted-foreground truncate">{categoryLabel}</p>
                        ) : null}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        className="shrink-0 gap-1"
                        disabled={atCap || addingId === business.id}
                        onClick={() => handleAdd(business.id)}
                      >
                        {addingId === business.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <LinkIcon className="h-4 w-4" />
                        )}
                        Connect
                      </Button>
                    </li>
                  );
                })}
              </ul>
            ) : unlinkedBusinesses.length === 1 ? (
              <Button
                type="button"
                variant="outline"
                className="gap-2 bg-background hover:bg-background/90"
                disabled={atCap || addingId === unlinkedBusinesses[0].id}
                onClick={() => handleAdd(unlinkedBusinesses[0].id)}
              >
                {addingId === unlinkedBusinesses[0].id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                List “{unlinkedBusinesses[0].name}”
              </Button>
            ) : (
              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                <Select
                  key={unlinkedBusinesses.map((b) => b.id).join(',')}
                  onValueChange={(id) => {
                    void handleAdd(id);
                  }}
                  disabled={atCap}
                >
                  <SelectTrigger className="w-full sm:max-w-md">
                    <SelectValue
                      placeholder={atCap ? 'Maximum listings reached' : 'Choose business…'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {unlinkedBusinesses.map((b) => (
                      <SelectItem key={b.id} value={b.id}>
                        {b.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {addingId && <Loader2 className="h-4 w-4 animate-spin shrink-0" />}
              </div>
            )}
          </div>
        )}

        {atCap && unlinkedBusinesses.length > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-500 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2">
            {isCompact
              ? `Limit reached (${maxN}). Remove a listing to add another.`
              : `You have reached the maximum of ${maxN} approved or pending listing${maxN === 1 ? '' : 's'} for this community. Remove a listing or ask an admin to raise the limit.`}
          </p>
        )}

        <div
          className={
            isCompact
              ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
              : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-foreground/25'
          }
        >
          {!isCompact ? (
            <p className="text-sm text-muted-foreground">
              {(user?.businesses?.length ?? 0) > 0
                ? 'Register another business on your profile, then list it here.'
                : 'Start by creating a business profile on your account.'}
            </p>
          ) : null}
          <Button
            asChild
            variant={isCompact ? 'default' : 'outline'}
            size="sm"
            className={isCompact ? 'w-full shrink-0 gap-2' : 'shrink-0 gap-2'}
          >
            <Link to={createBusinessHref}>
              <Plus className="h-4 w-4" />
              {isCompact
                ? 'Create new business'
                : (user?.businesses?.length ?? 0) > 0
                  ? 'Add another business'
                  : 'Create business profile'}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
